/**
 * customize-popup.js — Cart Customization Popup Component (Ticket C)
 *
 * Defines <cart-customize-popup>, a custom element that extends the theme's Drawer
 * (x-drawer / Drawer class defined in assets/theme.js.liquid). The extension pattern uses
 * customElements.whenDefined so that the definition works regardless of whether
 * theme.js (ES module, deferred) or this file (defer script) executes first.
 *
 * ── Public API (consumed by Ticket D — checkout interception) ─────────────────
 *
 *   const popup = document.getElementById('cart-customize-popup');
 *
 *   popup.eligibleItems
 *     Returns a fresh, de-duplicated array of eligible cart lines:
 *       [{ lineKey, title, image, quantity, builderUrl }, ...]
 *     De-duplicates by data-line-key — both the cart drawer and the cart page
 *     surface emit .cart-customize-data elements, so a cart page has every
 *     eligible line twice in the DOM. The Set-based de-duplication ensures the
 *     count and list are always accurate.
 *     Returns [] when data-popup-enabled="false" on the config wrapper.
 *
 *   popup.eligibleCount
 *     Convenience getter; equals popup.eligibleItems.length.
 *
 *   popup.show()
 *     Opens the popup. No-ops when eligibleCount === 0 (empty popup never opens).
 *     Inherited from Drawer/DialogElement; overridden here to add the guard.
 *
 *   popup.hide()
 *     Closes the popup. Inherited directly from Drawer/DialogElement.
 *
 * ── CustomEvent dispatched by this component ──────────────────────────────────
 *
 *   'cart-customize:decline'  (bubbles: true)
 *     Fired from the popup element immediately before hide() when the shopper
 *     clicks the No button. Ticket D (assets/customize-checkout.js) listens for
 *     this event at document level to resume the intercepted checkout submit.
 *
 *     The event is NOT fired when the popup is closed via the X button, overlay
 *     click, or Escape key — those are ambiguous dismissals. Only an explicit
 *     "No, proceed to checkout" intent dispatches it.
 *
 * ── State machine ─────────────────────────────────────────────────────────────
 *
 *   State 1 (initial, reset on every close):
 *     Heading (in drawer header slot) + count message + Yes / No buttons.
 *     Yes → State 2. No → hide().
 *
 *   State 2:
 *     Item-select heading + list of eligible products as clickable rows.
 *     Selecting a row: window.location.assign(item.builderUrl).
 *
 *   On dialog:after-hide: always reset to State 1.
 *
 * ── De-duplication reasoning ──────────────────────────────────────────────────
 *
 *   On the cart page (/cart), both sections/cart-drawer.liquid and
 *   sections/main-cart.liquid are rendered (the cart drawer is always present
 *   globally via overlay-group). Each emits a .cart-customize-config wrapper
 *   with one .cart-customize-data child per eligible line. So a cart with 2
 *   eligible lines produces 4 .cart-customize-data elements on the cart page.
 *   The Set keyed on data-line-key ensures eligibleItems always returns 2
 *   items, not 4.
 *
 * ── customElements.whenDefined ordering ──────────────────────────────────────
 *
 *   theme.js is a type="module" script (implicitly deferred). This file is
 *   loaded with defer (non-module). Both execute after DOM parsing completes,
 *   but execution order between them depends on network/parse timing.
 *
 *   Case A — theme.js runs first:
 *     x-drawer is already registered. whenDefined('x-drawer') resolves
 *     immediately. CartCustomizePopup is defined. The already-parsed
 *     <cart-customize-popup> element in the DOM is upgraded; connectedCallback
 *     fires.
 *
 *   Case B — this file runs first:
 *     whenDefined('x-drawer') returns a pending promise and queues the
 *     callback. When theme.js eventually registers x-drawer, the promise
 *     resolves, CartCustomizePopup is defined, and the element is upgraded.
 *
 *   Both orderings are safe. No race condition.
 *
 * ── What this file does NOT do ───────────────────────────────────────────────
 *
 *   - Does not touch the checkout form or any submit event (that is Ticket D).
 *   - Does not compose builder URLs from individual params — it reads
 *     data-builder-url verbatim from the data layer (Ticket B contract).
 *   - Does not register event listeners on page load or on any form submit.
 *   - Has no import/export statements; plain ES2020, no bundler required.
 */

customElements.whenDefined('x-drawer').then(() => {
  /**
   * CartCustomizePopup extends the theme's Drawer element, inheriting:
   *   - Shadow DOM shell from #drawer-default-template
   *   - Overlay click-to-close
   *   - Close button (shadow DOM)
   *   - Focus trapping
   *   - Scroll lock (shouldLock = true)
   *   - shouldAppendToBody = true (escapes stacking contexts)
   *   - Animated show/hide (clip-path transitions)
   *   - Escape key handling
   *   - Bottom-sheet on mobile (open-from="right" + sm-max breakpoint)
   *   - dialog:after-show / dialog:after-hide events
   */
  class CartCustomizePopup extends customElements.get('x-drawer') {

    connectedCallback() {
      super.connectedCallback();

      // Cache light-DOM element references (stable across shouldAppendToBody moves)
      this._state1Div   = this.querySelector('.ccp-state-1');
      this._state2Div   = this.querySelector('.ccp-state-2');
      this._messageEl   = this.querySelector('[data-ccp-message]');
      this._itemListEl  = this.querySelector('[data-ccp-item-list]');
      this._yesBtn      = this.querySelector('[data-ccp-yes]');
      this._noBtn       = this.querySelector('[data-ccp-no]');

      // IMPORTANT: every listener below is bound to this._abortController.signal
      // (created by super.connectedCallback(), aborted by disconnectedCallback).
      //
      // Why this matters: Drawer sets shouldAppendToBody = true, so DialogElement
      // moves this element to <body> on open and moves it BACK to its original
      // parent on hide (theme.js, attributeChangedCallback "open" branch). Each
      // move disconnects and reconnects the element, so connectedCallback runs
      // again on every open/close cycle. Without the signal the handlers would
      // accumulate without bound — one extra copy per cycle. This is the same
      // idiom Drawer itself uses for its overlay listener.
      const signal = this._abortController.signal;

      this._yesBtn?.addEventListener('click', () => this._toState2(), { signal });
      this._noBtn?.addEventListener('click', () => {
        // Signal Ticket D that the shopper explicitly chose "No, continue to
        // checkout" — distinct from Escape / X / overlay which are ambiguous.
        // Dispatch BEFORE hide() so the checkout handler can read pending
        // form references while the popup is still in its open state.
        this.dispatchEvent(new CustomEvent('cart-customize:decline', { bubbles: true }));
        this.hide();
      }, { signal });

      // Always reset to state 1 after the hide animation completes,
      // so a re-open never starts at a stale state 2 list.
      this.addEventListener('dialog:after-hide', () => this._toState1(), { signal });
    }

    // ── Public API ─────────────────────────────────────────────────────────

    /**
     * Returns a fresh, de-duplicated array of eligible cart items.
     *
     * Reads the DOM on every call rather than caching, because the cart drawer
     * re-renders in place via the Section Rendering API on a quantity change,
     * swapping out the .cart-customize-data elements with no page reload. (The
     * cart page instead does a full navigation for the same action, which is
     * also fine — the fresh read simply happens after the reload.)
     *
     * Returns [] when data-popup-enabled="false" on the config wrapper,
     * treating a disabled flag the same as zero eligible lines.
     *
     * @returns {{ lineKey: string, title: string, image: string|null,
     *             quantity: number, builderUrl: string }[]}
     */
    get eligibleItems() {
      // Check the popup-enabled flag. Both surfaces share the same setting
      // value, so reading the first config wrapper found is authoritative.
      const config = document.querySelector('.cart-customize-config');
      if (!config || config.dataset.popupEnabled === 'false') return [];

      const seen  = new Set();
      const items = [];

      document.querySelectorAll('.cart-customize-data').forEach(el => {
        const key = el.dataset.lineKey;
        if (!key || seen.has(key)) return; // skip missing keys and duplicates
        seen.add(key);

        items.push({
          lineKey:    key,
          title:      el.dataset.productTitle  || '',
          image:      el.dataset.productImage  || null, // attribute absent = null
          quantity:   parseInt(el.dataset.quantity, 10) || 1,
          builderUrl: el.dataset.builderUrl    || ''
        });
      });

      return items;
    }

    /**
     * Convenience count. 0 when the popup is disabled or no lines are eligible.
     * @returns {number}
     */
    get eligibleCount() {
      return this.eligibleItems.length;
    }

    /**
     * Override show() to no-op when there are no eligible items.
     * Ticket D must be able to call .show() unconditionally and trust that
     * an empty popup will never appear.
     */
    show(animate = true) {
      if (this.eligibleCount === 0) return;
      this._updateState1Message();
      return super.show(animate);
    }

    // ── Internal state machine ──────────────────────────────────────────────

    /**
     * Update the message paragraph in state 1 with the current eligible count.
     * Uses the singular message when count === 1 to avoid "1 products".
     * Substitutes the literal token [X] in the plural template.
     */
    _updateState1Message() {
      if (!this._messageEl) return;

      const count = this.eligibleCount;
      let msg;

      // Replace EVERY occurrence of the token, not just the first — a merchant may
      // reasonably write the count twice ("You have [X] items… customize all [X]?").
      const fill = (template, value) => (template || '').split('[X]').join(value);

      if (count === 1) {
        // Use the dedicated singular setting; fall back to substituting [X] in
        // the plural message if the merchant has blanked the singular one.
        const singular = (this.dataset.popupMessageSingular || '').trim();
        msg = singular || fill(this.dataset.popupMessage, '1');
      } else {
        msg = fill(this.dataset.popupMessage, String(count));
      }

      this._messageEl.textContent = msg;
    }

    /**
     * Transition to State 2: build the item list then swap visibility.
     */
    _toState2() {
      this._buildItemList();
      if (this._state1Div) this._state1Div.hidden = true;
      if (this._state2Div) this._state2Div.hidden = false;
      // Move focus to the first item button for keyboard users
      const firstItem = this._itemListEl?.querySelector('.ccp-item');
      firstItem?.focus();
    }

    /**
     * Reset to State 1. Called after every hide so re-opens start fresh.
     */
    _toState1() {
      if (this._state2Div) this._state2Div.hidden = true;
      if (this._state1Div) this._state1Div.hidden = false;
      // Clear the stale item list so it isn't briefly visible on next Yes click
      if (this._itemListEl) this._itemListEl.innerHTML = '';
    }

    /**
     * Build the item-selection list from the current eligibleItems.
     * Always clears the list first (cart may have changed since last open).
     */
    _buildItemList() {
      if (!this._itemListEl) return;
      this._itemListEl.innerHTML = '';

      this.eligibleItems.forEach(item => {
        const li  = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ccp-item';
        // aria-label gives screen readers the full product name since the
        // image alt and title span together may be read as a run-on sentence.
        btn.setAttribute('aria-label', item.title);

        btn.addEventListener('click', () => {
          // Navigate to the pre-composed deep-link from the data layer.
          // We read data-builder-url verbatim — never re-compose from parts.
          window.location.assign(item.builderUrl);
        });

        // ── Thumbnail ─────────────────────────────────────────────────────
        if (item.image) {
          const img = document.createElement('img');
          img.src     = item.image;
          img.alt     = item.title;
          img.className = 'ccp-item__image';
          img.width   = 48;
          img.height  = 48;
          img.loading = 'lazy';
          img.decoding = 'async';
          btn.appendChild(img);
        } else {
          // data-product-image attribute is legitimately absent for some lines
          const placeholder = document.createElement('div');
          placeholder.className = 'ccp-item__placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          btn.appendChild(placeholder);
        }

        // ── Product title ─────────────────────────────────────────────────
        const titleEl = document.createElement('span');
        titleEl.className   = 'ccp-item__title';
        titleEl.textContent = item.title;
        btn.appendChild(titleEl);

        // ── Quantity badge (only when qty > 1) ────────────────────────────
        if (item.quantity > 1) {
          const qtyEl = document.createElement('span');
          qtyEl.className   = 'ccp-item__qty';
          qtyEl.textContent = '× ' + item.quantity; // "× 2"
          btn.appendChild(qtyEl);
        }

        li.appendChild(btn);
        this._itemListEl.appendChild(li);
      });
    }
  }

  // Guard against double-registration (e.g. HMR or duplicate script loads)
  if (!customElements.get('cart-customize-popup')) {
    customElements.define('cart-customize-popup', CartCustomizePopup);
  }
});
